import { useLayoutEffect, useMemo, useRef } from "react";

const useScrollPosition = (ref: React.RefObject<HTMLDivElement>, key: string, body: any[], isLoading: boolean) => {
  // Создаем уникальный ключ на основе данных для отслеживания изменений
  const bodyIds = useMemo(() => body.map((item) => item.ID).join(","), [body]);
  const prevBodyIdsRef = useRef<string>("");

  // Сохраняем позицию скролла при прокрутке
  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;

    const handleScroll = () => {
      if (container && container.scrollTop > 0) {
        sessionStorage.setItem(key, container.scrollTop.toString());
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [ref, key]);

  // Сохраняем скролл перед тем, как данные очистятся
  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;

    // Если данные были, а теперь их нет (очистка), сохраняем текущую позицию
    if (prevBodyIdsRef.current && body.length === 0) {
      const currentScroll = container.scrollTop;
      if (currentScroll > 0) {
        sessionStorage.setItem(key, currentScroll.toString());
      }
    }

    // Обновляем предыдущее значение
    prevBodyIdsRef.current = bodyIds;
  }, [bodyIds, body.length, key, ref]);

  // Восстанавливаем позицию скролла при изменении данных
  useLayoutEffect(() => {
    // Не восстанавливаем скролл во время загрузки или если данных нет
    if (isLoading || body.length === 0) return;

    const savedScrollTop = sessionStorage.getItem(key);
    if (!savedScrollTop) return;

    const container = ref.current;
    if (!container) return;

    const savedValue = parseInt(savedScrollTop, 10);
    if (savedValue === 0) return;

    // Ждем, пока появится скролл (контент отрендерится)
    let attempts = 0;
    const maxAttempts = 25;

    const restoreScroll = () => {
      if (!ref.current) return;

      const hasScrollableHeight = ref.current.scrollHeight > ref.current.clientHeight;
      const currentScrollTop = ref.current.scrollTop;

      // Если скролл уже на нужной позиции, не трогаем
      if (Math.abs(currentScrollTop - savedValue) < 5) {
        return;
      }

      if (!hasScrollableHeight && attempts < maxAttempts) {
        attempts += 1;
        requestAnimationFrame(restoreScroll);
        return;
      }

      // Восстанавливаем только если есть скролл и позиция отличается
      if (hasScrollableHeight && Math.abs(currentScrollTop - savedValue) >= 5) {
        ref.current.scrollTop = savedValue;
      }
    };

    // Увеличиваем задержку для надежности
    const timeoutId = setTimeout(() => {
      restoreScroll();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [ref, key, bodyIds, isLoading, body.length]);
};

export default useScrollPosition;
